import { useMemo, useRef, useState, type RefObject } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  BarChart3,
  Folder,
  Gauge,
  MoreHorizontal,
  Plus,
  Trash2,
  TriangleAlert,
  X,
} from "lucide-react";
import {
  ActionMenu,
  Badge,
  Button,
  Checkbox,
  DataTable,
  DateRangeInput,
  FeedbackState,
  Field,
  FormDrawer,
  localTablePage,
  MultiSelect,
  PageContent,
  PageHeader,
  PageShell,
  SearchInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  StatusBadge,
  TableIdentity,
  updateTableState,
  type DataTableColumn,
  type DataTableState,
} from "@h2j-data-team/ui";
import type { QlikApp, AppAnalysisListItem, AppAnalysisStatus } from "@repo/shared";

import { ConfirmDialog, useConfirmDialog } from "@/components/shared/confirm-dialog";
import { toast } from "@/components/shared/toast";
import { DensityToggle } from "@/components/density-toggle";
import { HealthGradeBadge } from "@/components/app-analysis/HealthGradeBadge";
import { QlikEvaluationRankingDrawer } from "@/components/app-analysis/QlikEvaluationRankingDrawer";
import { loadErrorState } from "@/components/app-analysis/load-error";
import {
  appAnalysisListInfiniteQueryOptions,
  useDeleteAnalysis,
  useStartAppAnalysis,
} from "@/features/app-analysis/api";
import {
  connectionAppsQueryOptions,
  connectionsQueryOptions,
} from "@/features/qlik-connections/api";
import { useFormatDate } from "@/hooks/use-format-date";
import { getErrorMessage } from "@/lib/api-client";
import { formatBytes } from "@/lib/health-format";
import { useSearchHotkey } from "@/lib/use-search-hotkey";
import { useTableDensity } from "@/lib/use-table-density";
import { listRefreshing, paginationLabels, pickerMessages, tableMessages } from "@/lib/ui-labels";

// Status → badge tone, mirroring the QVD analyses list so both surfaces read
// the same.
const STATUS_TONE: Record<AppAnalysisStatus, "info" | "success" | "danger" | "neutral"> = {
  processing: "info",
  completed: "success",
  failed: "danger",
  cancelled: "neutral",
};

const PAGE_SIZES = [10, 25, 50, 100];

// Invalid date strings in a hand-edited URL must not crash the render —
// treat them as "no filter" instead of letting toISOString() throw.
function toLocalDayIso(date: string | undefined, timeSuffix: string): string | undefined {
  if (!date) return undefined;
  const d = new Date(`${date}T${timeSuffix}`);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

export const Route = createFileRoute("/_authenticated/analyses/")({
  staticData: { pageTitle: "breadcrumbs.analyses" },
  validateSearch: (search: Record<string, unknown>) => ({
    connections: typeof search.connections === "string" ? search.connections : undefined,
    appName: typeof search.appName === "string" ? search.appName : undefined,
    dateFrom: typeof search.dateFrom === "string" ? search.dateFrom : undefined,
    dateTo: typeof search.dateTo === "string" ? search.dateTo : undefined,
  }),
  component: AnalysesPage,
});

function NewAnalysisDialog({
  open,
  onOpenChange,
  restoreFocusRef,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  restoreFocusRef: RefObject<HTMLElement | null>;
}) {
  const { t } = useTranslation("analyses");
  const { t: tc } = useTranslation("common");
  const navigate = useNavigate();
  const startAnalysis = useStartAppAnalysis();
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  const [selectedApp, setSelectedApp] = useState<QlikApp | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpaces, setSelectedSpaces] = useState<string[]>([]);
  // Qlik's evaluation is the product default (it costs us nothing and runs on
  // Qlik's side); our own measurement is not (it opens the app with data).
  const [includeQlikEvaluation, setIncludeQlikEvaluation] = useState(true);
  const [includeObjectMeasurement, setIncludeObjectMeasurement] = useState(false);

  const { data: connections, isLoading: isLoadingConnections } = useQuery({
    ...connectionsQueryOptions,
    enabled: open,
  });

  const {
    data: apps,
    isLoading: isLoadingApps,
    isError: appsFailed,
    refetch: refetchApps,
  } = useQuery({
    ...connectionAppsQueryOptions(selectedConnectionId),
    enabled: !!selectedConnectionId,
  });

  const spaceOptions = useMemo(() => {
    if (!apps) return [];
    const spaces = new Set<string>();
    apps.forEach((app) => {
      if (app.spaceName) spaces.add(app.spaceName);
    });
    return Array.from(spaces)
      .sort()
      .map((space) => ({ label: space, value: space }));
  }, [apps]);

  const filteredApps = useMemo(() => {
    if (!apps) return [];
    const lower = searchQuery.toLowerCase();
    return apps.filter((app) => {
      const matchesSearch =
        !searchQuery ||
        app.name.toLowerCase().includes(lower) ||
        (app.spaceName?.toLowerCase().includes(lower) ?? false);
      const matchesSpace =
        selectedSpaces.length === 0 || (!!app.spaceName && selectedSpaces.includes(app.spaceName));
      return matchesSearch && matchesSpace;
    });
  }, [apps, searchQuery, selectedSpaces]);

  const handleClose = () => {
    onOpenChange(false);
    setSelectedConnectionId("");
    setSelectedApp(null);
    setSearchQuery("");
    setSelectedSpaces([]);
    setIncludeQlikEvaluation(true);
    setIncludeObjectMeasurement(false);
  };

  const handleAnalyze = async () => {
    if (!selectedApp || !selectedConnectionId) return;
    try {
      const analysis = await startAnalysis.mutateAsync({
        connectionId: selectedConnectionId,
        appId: selectedApp.id,
        appName: selectedApp.name,
        spaceName: selectedApp.spaceName ?? undefined,
        includeQlikEvaluation,
        includeObjectMeasurement,
      });
      handleClose();
      navigate({ to: "/analyses/$analysisId", params: { analysisId: analysis.id } });
    } catch (err) {
      // Surfaces the "already running" 409 and any other trigger error as a toast.
      toast.error(getErrorMessage(err, t("dialog.jobFailed")));
    }
  };

  const pending = startAnalysis.isPending;

  return (
    <FormDrawer
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
      title={t("dialog.title")}
      description={t("dialog.description")}
      closeLabel={tc("close")}
      restoreFocusRef={restoreFocusRef}
      busy={pending}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            {t("dialog.cancel")}
          </Button>
          <Button onClick={() => void handleAnalyze()} loading={pending} disabled={!selectedApp}>
            {pending ? t("dialog.starting") : t("dialog.analyze")}
          </Button>
        </div>
      }
    >
      {/* No <form>: starting an analysis spends Qlik quota, so Enter in the app
          search must not start one — only the button does. */}
      <div className="space-y-4" aria-busy={pending || undefined}>
        <Field label={t("dialog.connectionLabel")}>
          {(control) => (
            <Select
              value={selectedConnectionId}
              onValueChange={(value) => {
                setSelectedConnectionId(value);
                setSelectedApp(null);
                setSearchQuery("");
                setSelectedSpaces([]);
              }}
            >
              <SelectTrigger {...control}>
                <SelectValue
                  placeholder={
                    isLoadingConnections
                      ? t("dialog.connectionLoading")
                      : t("dialog.connectionPlaceholder")
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {connections?.map((conn) => (
                  <SelectItem key={conn.id} value={conn.id}>
                    {conn.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>

        {selectedConnectionId && (
          <div className="space-y-2">
            <span id="new-analysis-app-label" className="text-sm font-medium">
              {t("dialog.appLabel")}
            </span>
            <SearchInput
              aria-label={t("dialog.appSearch")}
              placeholder={t("dialog.appSearch")}
              clearLabel={tc("list.clearSearch")}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            {spaceOptions.length > 1 && (
              <MultiSelect
                aria-label={t("dialog.spaceFilterPlaceholder")}
                options={spaceOptions}
                value={selectedSpaces}
                onValueChange={setSelectedSpaces}
                messages={pickerMessages({ placeholder: t("dialog.spaceFilterPlaceholder") })}
                modal
              />
            )}
            {appsFailed ? (
              <FeedbackState
                state="error"
                title={t("dialog.appsFailed")}
                action={
                  <Button variant="outline" size="sm" onClick={() => void refetchApps()}>
                    {tc("tryAgain")}
                  </Button>
                }
              />
            ) : (
              <div
                role="radiogroup"
                aria-labelledby="new-analysis-app-label"
                aria-busy={isLoadingApps || undefined}
                className="max-h-[300px] overflow-y-auto rounded-lg border border-hairline"
              >
                {isLoadingApps ? (
                  <div className="space-y-2 p-3">
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                ) : filteredApps.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-dim">
                    {searchQuery || selectedSpaces.length > 0
                      ? t("dialog.noAppsMatch")
                      : t("dialog.noApps")}
                  </div>
                ) : (
                  <div className="divide-y divide-hairline">
                    {filteredApps.map((app) => (
                      <label
                        key={app.id}
                        className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-surface-muted has-checked:bg-brand-subtle has-focus-visible:outline-2 has-focus-visible:-outline-offset-2 has-focus-visible:outline-ring"
                      >
                        <input
                          type="radio"
                          name="analysis-app"
                          className="peer sr-only"
                          checked={selectedApp?.id === app.id}
                          onChange={() => setSelectedApp(app)}
                        />
                        <span
                          aria-hidden
                          className="flex size-4 shrink-0 items-center justify-center rounded-full border-2 border-border-strong peer-checked:border-brand peer-checked:bg-brand"
                        >
                          {selectedApp?.id === app.id && (
                            <span className="size-1.5 rounded-full bg-white" />
                          )}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{app.name}</span>
                          <span className="flex items-center gap-2 text-xs text-dim">
                            {app.spaceName && (
                              <span className="flex items-center gap-1">
                                <Folder className="size-3" aria-hidden />
                                {app.spaceName}
                              </span>
                            )}
                            {app.published && (
                              <Badge variant="secondary">{t("dialog.published")}</Badge>
                            )}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Optional phases. Both cost time somewhere, so both say where. */}
        <div className="space-y-3 rounded-lg border border-hairline px-4 py-3">
          <Field
            inline
            label={t("dialog.qlikEvaluationLabel")}
            hint={t("dialog.qlikEvaluationHelp")}
          >
            <Checkbox
              checked={includeQlikEvaluation}
              onCheckedChange={(checked) => setIncludeQlikEvaluation(checked === true)}
            />
          </Field>
          <Field
            inline
            label={t("dialog.objectMeasurementLabel")}
            hint={t("dialog.objectMeasurementHelp")}
          >
            <Checkbox
              checked={includeObjectMeasurement}
              onCheckedChange={(checked) => setIncludeObjectMeasurement(checked === true)}
            />
          </Field>
        </div>
      </div>
    </FormDrawer>
  );
}

/** The "…" menu of a row: one destructive action, which asks first. */
function RowMenu({
  analysis,
  deleting,
  onDelete,
}: {
  analysis: AppAnalysisListItem;
  deleting: boolean;
  onDelete: (id: string, appName: string, trigger: HTMLElement | null) => void;
}) {
  const { t } = useTranslation("analyses");
  const trigger = useRef<HTMLButtonElement>(null);
  const label = t("list.rowActions", { name: analysis.appName });
  return (
    <ActionMenu
      label={label}
      trigger={
        <Button ref={trigger} variant="ghost" size="icon-compact" aria-label={label}>
          <MoreHorizontal />
        </Button>
      }
      items={[
        {
          id: "delete",
          label: t("list.deleteLabel"),
          icon: <Trash2 />,
          destructive: true,
          disabled: deleting,
          onSelect: () => onDelete(analysis.id, analysis.appName, trigger.current),
        },
      ]}
    />
  );
}

type ListFilters = { connections: string[]; dateFrom?: string; dateTo?: string };

// The API takes the newest page of this size; the person pulls older ones.
const LOAD_SIZE = 50;

function AnalysesPage() {
  const { t } = useTranslation("analyses");
  const { t: tc } = useTranslation("common");
  const { formatDate } = useFormatDate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [rankingOpen, setRankingOpen] = useState(false);
  const { connections, appName, dateFrom, dateTo } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { density, setDensity } = useTableDensity();
  const searchRef = useRef<HTMLInputElement>(null);
  useSearchHotkey(searchRef);
  // The drawers have no trigger of their own: focus goes back to the button that opened them.
  const newAnalysisButton = useRef<HTMLButtonElement>(null);
  const rankingButton = useRef<HTMLButtonElement>(null);
  // The search and the filters live in the URL; the page and its size here.
  const [paging, setPaging] = useState({ page: 1, pageSize: 25 });

  const updateSearch = (patch: Record<string, string | undefined>) => {
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  };

  const { data: connectionsData } = useQuery(connectionsQueryOptions);

  const connectionOptions = useMemo(
    () => (connectionsData ?? []).map((c) => ({ label: c.name, value: c.id })),
    [connectionsData],
  );

  // The connections of the filter, from the URL's comma-separated list.
  const selectedConnections = useMemo(
    () =>
      (connections ?? "")
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean),
    [connections],
  );

  // The API takes one connection: with exactly one selected it filters there,
  // with several the loaded analyses are filtered on the client.
  const apiConnectionId = selectedConnections.length === 1 ? selectedConnections[0] : undefined;

  const state: DataTableState<ListFilters> = {
    page: paging.page,
    pageSize: paging.pageSize,
    query: appName ?? "",
    filters: { connections: selectedConnections, dateFrom, dateTo },
    selection: [],
  };

  const list = useInfiniteQuery(
    appAnalysisListInfiniteQueryOptions({
      limit: LOAD_SIZE,
      connectionId: apiConnectionId,
      // Native date inputs yield YYYY-MM-DD; the API expects full ISO datetimes.
      // Convert to the local day's bounds so "same day" filters behave as expected.
      dateFrom: toLocalDayIso(dateFrom, "00:00:00"),
      dateTo: toLocalDayIso(dateTo, "23:59:59.999"),
    }),
  );
  const { hasNextPage, fetchNextPage, isFetchingNextPage } = list;
  const analyses = useMemo(() => list.data?.pages.flatMap((p) => p.data) ?? [], [list.data]);

  const deleteMutation = useDeleteAnalysis();
  const { confirm, dialogProps } = useConfirmDialog();

  const connectionMap = useMemo(() => {
    const map = new Map<string, string>();
    connectionsData?.forEach((c) => map.set(c.id, c.name));
    return map;
  }, [connectionsData]);

  const hasActiveFilters = !!connections || !!appName || !!dateFrom || !!dateTo;

  function onTableChange(next: DataTableState<ListFilters>) {
    setPaging({ page: next.page, pageSize: next.pageSize });
    const patch: Record<string, string | undefined> = {};
    if (next.query !== state.query) patch.appName = next.query || undefined;
    if (next.filters !== state.filters) {
      const f = next.filters;
      const csv = f.connections.length ? f.connections.join(",") : undefined;
      if (csv !== connections) patch.connections = csv;
      if (f.dateFrom !== dateFrom) patch.dateFrom = f.dateFrom;
      if (f.dateTo !== dateTo) patch.dateTo = f.dateTo;
    }
    if (Object.keys(patch).length > 0) updateSearch(patch);
  }
  const changeTable = (patch: Partial<DataTableState<ListFilters>>) =>
    onTableChange(updateTableState(state, patch));

  const clearFilters = () =>
    changeTable({
      query: "",
      filters: { connections: [], dateFrom: undefined, dateTo: undefined },
    });

  const handleDelete = (id: string, appNameLabel: string, trigger: HTMLElement | null) => {
    confirm({
      // The menu is gone by the time the dialog closes: focus goes back to its button.
      restoreFocusRef: { current: trigger },
      title: t("delete.title"),
      description: t("delete.description", { appName: appNameLabel }),
      confirmLabel: t("delete.confirmLabel"),
      variant: "destructive",
      onConfirm: async () => {
        await deleteMutation.mutateAsync(id);
      },
    });
  };

  const columns: DataTableColumn<AppAnalysisListItem>[] = [
    {
      id: "app",
      header: t("table.app"),
      rowHeader: true,
      rowLink: true,
      variant: "identity",
      cell: (analysis) => (
        <TableIdentity
          leading={<BarChart3 />}
          name={
            <Link
              to="/analyses/$analysisId"
              params={{ analysisId: analysis.id }}
              className="hover:underline"
              title={analysis.appName}
            >
              {analysis.appName}
            </Link>
          }
          detail={
            analysis.spaceName && (
              <span className="flex items-center gap-1">
                <Folder className="size-3 text-faint" strokeWidth={1.8} aria-hidden />
                {analysis.spaceName}
              </span>
            )
          }
          detailMono={false}
        />
      ),
    },
    {
      id: "connection",
      header: t("table.connection"),
      width: 170,
      cell: (analysis) => (
        <span className="text-dim">{connectionMap.get(analysis.connectionId) ?? "—"}</span>
      ),
    },
    {
      id: "status",
      header: t("table.status"),
      width: 130,
      cell: (analysis) => {
        const status = analysis.status ?? "completed";
        return <StatusBadge tone={STATUS_TONE[status]}>{t(`status.${status}`)}</StatusBadge>;
      },
    },
    {
      id: "health",
      header: t("table.health"),
      width: 140,
      cell: (analysis) =>
        analysis.health ? (
          <HealthGradeBadge grade={analysis.health.grade} score={analysis.health.score} />
        ) : (
          <span className="text-faint">—</span>
        ),
    },
    {
      id: "fields",
      header: t("table.fields"),
      align: "end",
      width: 190,
      // A processing/failed/cancelled placeholder carries no result payload.
      cell: ({ summary }) =>
        summary ? (
          <>
            <span className="font-mono text-dim">{summary.totalFields}</span>
            {summary.unusedFields > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-status-warning-foreground">
                <TriangleAlert className="size-3" strokeWidth={1.8} aria-hidden />
                {t("list.unused", { count: summary.unusedFields })}
              </span>
            )}
          </>
        ) : (
          <span className="text-faint">—</span>
        ),
    },
    {
      id: "size",
      header: t("table.size"),
      align: "end",
      variant: "numeric",
      width: 110,
      cell: ({ summary }) => (
        <span className="text-dim">
          {summary
            ? formatBytes(summary.appFileSize > 0 ? summary.appFileSize : summary.totalBytes)
            : "—"}
        </span>
      ),
    },
    {
      id: "analyzed",
      header: t("table.analyzed"),
      variant: "numeric",
      width: 130,
      cell: (analysis) => <span className="text-dim">{formatDate(analysis.analyzedAt)}</span>,
    },
  ];

  // The API filters by one connection and a period; the app name search and
  // several connections narrow the analyses loaded so far, here.
  const page = localTablePage(analyses, state, {
    columns,
    search: (analysis) => analysis.appName,
    filter: (analysis, f) =>
      f.connections.length <= 1 || f.connections.includes(analysis.connectionId),
  });

  return (
    <>
      <PageShell
        header={
          <PageHeader
            title={t("list.pageTitle")}
            description={t("list.description")}
            actions={
              <>
                <Button
                  ref={rankingButton}
                  variant="outline"
                  size="sm"
                  onClick={() => setRankingOpen(true)}
                >
                  <Gauge />
                  {t("list.performanceRanking")}
                </Button>
                <Button ref={newAnalysisButton} size="sm" onClick={() => setDialogOpen(true)}>
                  <Plus />
                  {t("list.newAnalysis")}
                </Button>
              </>
            }
          />
        }
      >
        <PageContent fill>
          <DataTable
            label={t("list.pageTitle")}
            columns={columns}
            rows={page.rows}
            getRowId={(analysis) => analysis.id}
            state={state}
            onStateChange={onTableChange}
            density={density}
            loading={list.isPending}
            refreshing={listRefreshing(list)}
            error={
              list.isError
                ? loadErrorState(list.error, t("list.failedToLoad"), tc("error.supportCode"))
                : null
            }
            onRetry={() => void list.refetch()}
            search={{
              label: t("list.filterByApp"),
              clearLabel: tc("list.clearSearch"),
              placeholder: t("list.filterByApp"),
              inputRef: searchRef,
              shortcutHint: "/",
              debounceMs: 300,
            }}
            toolbar={
              <>
                <MultiSelect
                  size="compact"
                  className="w-[200px]"
                  aria-label={t("list.allConnections")}
                  options={connectionOptions}
                  value={selectedConnections}
                  onValueChange={(next) =>
                    changeTable({ filters: { ...state.filters, connections: next } })
                  }
                  messages={pickerMessages({ placeholder: t("list.allConnections") })}
                  maxDisplay={1}
                />
                <DateRangeInput
                  size="compact"
                  aria-label={t("list.dateRange")}
                  fromLabel={t("list.dateFrom")}
                  toLabel={t("list.dateTo")}
                  clearLabel={t("list.clearDates")}
                  value={{ from: dateFrom, to: dateTo }}
                  onValueChange={({ from, to }) =>
                    changeTable({
                      filters: {
                        ...state.filters,
                        dateFrom: from || undefined,
                        dateTo: to || undefined,
                      },
                    })
                  }
                />
                {hasActiveFilters && (
                  <Button variant="ghost" size="compact" onClick={clearFilters}>
                    <X />
                    {t("list.clearFilters")}
                  </Button>
                )}
                <DensityToggle value={density} onChange={setDensity} />
              </>
            }
            footer={
              hasNextPage ? (
                <div className="flex items-center gap-3">
                  <p className="text-dim">{t("list.loadedCount", { count: analyses.length })}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    loading={isFetchingNextPage}
                    onClick={() => void fetchNextPage()}
                  >
                    {t("list.loadOlder")}
                  </Button>
                </div>
              ) : undefined
            }
            pagination={{
              kind: "offset",
              total: page.total,
              pageSizeOptions: PAGE_SIZES,
              labels: paginationLabels(),
            }}
            rowActions={{
              label: t("list.rowActionsColumn"),
              width: 56,
              render: (analysis) => (
                <RowMenu
                  analysis={analysis}
                  deleting={deleteMutation.isPending}
                  onDelete={handleDelete}
                />
              ),
            }}
            messages={tableMessages({
              empty: {
                title: t("list.empty.title"),
                description: t("list.empty.description"),
                action: (
                  <Button onClick={() => setDialogOpen(true)}>
                    <Plus />
                    {t("list.empty.action")}
                  </Button>
                ),
              },
              noResults: {
                title: t("list.noMatch.title"),
                description: t("list.noMatch.description"),
                action: (
                  <Button variant="outline" onClick={clearFilters}>
                    {t("list.noMatch.clearFilters")}
                  </Button>
                ),
              },
            })}
          />
        </PageContent>
      </PageShell>

      <NewAnalysisDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        restoreFocusRef={newAnalysisButton}
      />
      <QlikEvaluationRankingDrawer
        open={rankingOpen}
        onOpenChange={setRankingOpen}
        restoreFocusRef={rankingButton}
        defaultConnectionId={apiConnectionId}
      />
      <ConfirmDialog {...dialogProps} isPending={deleteMutation.isPending} />
    </>
  );
}
